import React, { Fragment } from 'react';

import { ContactEntity } from '~/components/Contact';
import { HandWithCoinsIcon } from '~/components/icons';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import { getDateFromServerDateTime } from '~/DateTimeUtils';
import type { Exposure } from '~/server_shared/swagger/data-contracts';
import formatDaysAsWeeksAndDays from '~/Utils/formatDaysAsWeekAndDays';

import { AmountValueDisplay } from './helpers';
import IncomeProtectionFieldDisplay from './IncomeProtectionFieldDisplay';
import TEXTS from './texts';
import type { IncomeProtectionDASummaryDisplayKeysType, IncomeProtectionDaSummaryType } from './types';

interface IncomeProtectionDAScreenSummaryProps {
  summary: IncomeProtectionDaSummaryType;
  exposure: Exposure & { id: number };
  claim: ClaimPolymorph;
}

const SummaryIcon = () => {
  return (
    <div className="me-4 inline-flex rounded-md bg-purple-100 p-[18px] text-purple-700">
      <HandWithCoinsIcon size={24} iconColor="currentColor" />
    </div>
  );
};

const DISPLAY_FIELDS = [
  {
    key: 'exposure_number',
    label: TEXTS.summaryDisplayLabels.exposure_number,
    displayValue: (summaryData: IncomeProtectionDaSummaryType) => summaryData.exposure_number,
  },
  {
    key: 'claimant_id',
    label: TEXTS.summaryDisplayLabels.claimant_id,
    displayValue: (summaryData: IncomeProtectionDaSummaryType) =>
      summaryData.claimant_id ? (
        <ContactEntity contactId={summaryData.claimant_id} contactDisplayName={summaryData.claimant_full_name} />
      ) : (
        TEXTS.notAvailable
      ),
  },
  {
    key: 'total_paid',
    label: TEXTS.summaryDisplayLabels.total_paid,
    displayValue: (summaryData: IncomeProtectionDaSummaryType) => (
      <AmountValueDisplay amount={summaryData.total_paid} />
    ),
  },
  {
    key: 'total_incurred',
    label: TEXTS.summaryDisplayLabels.total_incurred,
    displayValue: (summaryData: IncomeProtectionDaSummaryType) => (
      <AmountValueDisplay amount={summaryData.total_incurred} />
    ),
  },
  {
    key: 'days_paid',
    label: TEXTS.summaryDisplayLabels.days_paid,
    displayValue: (summaryData: IncomeProtectionDaSummaryType) => (
      <span className="capitalize">{formatDaysAsWeeksAndDays(summaryData.days_paid)}</span>
    ),
  },
  {
    key: 'days_remaining',
    label: TEXTS.summaryDisplayLabels.days_remaining,
    displayValue: (summaryData: IncomeProtectionDaSummaryType) => (
      <span className="capitalize">{formatDaysAsWeeksAndDays(summaryData.days_remaining)}</span>
    ),
  },
  {
    key: 'medical_certificate_expiry_date',
    label: TEXTS.summaryDisplayLabels.medical_certificate_expiry_date,
    displayValue: (summaryData: IncomeProtectionDaSummaryType) =>
      summaryData.medical_certificate_expiry_date
        ? getDateFromServerDateTime(summaryData.medical_certificate_expiry_date)
        : TEXTS.emptyValue,
  },
] as const satisfies {
  key: IncomeProtectionDASummaryDisplayKeysType;
  label: string;
  displayValue: (summaryData: IncomeProtectionDaSummaryType) => React.ReactNode;
}[];

const IncomeProtectionDAScreenSummary = ({ summary }: IncomeProtectionDAScreenSummaryProps) => {
  return (
    <div className="flex items-center gap-20 overflow-x-auto rounded-xl bg-white px-40 py-24">
      <SummaryIcon />
      {DISPLAY_FIELDS.map((field, index) => (
        <Fragment key={field.key}>
          <IncomeProtectionFieldDisplay label={field.label} valueDisplay={field.displayValue(summary)} />
          {index < DISPLAY_FIELDS.length - 1 && <div className="w-[1px] flex-shrink-0 self-stretch bg-slate-600" />}
        </Fragment>
      ))}
    </div>
  );
};

export default IncomeProtectionDAScreenSummary;
